import { createLogger } from '@/lib/logger'

import { clearMobileStateRetry } from './auth/mobile-state-handoff'

const logger = createLogger('utils/mobile-auth')

/**
 * Mobile Authentication Utilities
 * Handles mobile app authentication flow via web login
 */

const MOBILE_PLATFORM_KEY = 'mobile_auth_platform'
const MOBILE_REDIRECT_KEY = 'mobile_auth_redirect'
const MOBILE_OAUTH_STATE_KEY = 'mobile_auth_oauth_state_v1'
const ALLOWED_MOBILE_SCHEMES = new Set([
  'scrambl',
  'scrambl-dev',
  'scrambl-local',
])

export interface MobileAuthContext {
  platform: 'mobile'
  redirect: string
}

function isAllowedMobileRedirect(redirect: string): boolean {
  try {
    const parsed = new URL(redirect)
    const scheme = parsed.protocol.replace(':', '').toLowerCase()
    const host = parsed.hostname.toLowerCase()
    const path = parsed.pathname

    if (
      parsed.username ||
      parsed.password ||
      parsed.port ||
      parsed.hash ||
      ['state', 'codeChallenge', 'codeChallengeMethod'].some(
        key => parsed.searchParams.getAll(key).length > 1,
      ) ||
      [
        'accessToken',
        'refreshToken',
        'idToken',
        'codeVerifier',
        'code',
        'handoffCode',
      ].some(key => parsed.searchParams.has(key))
    )
      return false
    if (!ALLOWED_MOBILE_SCHEMES.has(scheme)) {
      return false
    }

    // Accept:
    // - scrambl://auth/callback  -> host=auth, path=/callback
    // - scrambl:///auth/callback -> host="", path=/auth/callback
    const isExpectedCallbackPath =
      (host === 'auth' && path === '/callback') ||
      (host === '' && path === '/auth/callback')

    return isExpectedCallbackPath
  } catch {
    return false
  }
}

/**
 * Check if current request is from mobile app
 */
export function isMobileAuthFlow(): boolean {
  return sessionStorage.getItem(MOBILE_PLATFORM_KEY) === 'mobile'
}

/** Preserve a validated native entry before the login component stores its context. */
export function isMobileAuthEntry(search: string): boolean {
  const params = new URLSearchParams(search)
  if (params.get('platform') === 'mobile')
    return isAllowedMobileRedirect(params.get('redirect') ?? '')
  return isMobileAuthFlow()
}

/**
 * Get stored mobile auth context
 */
export function getMobileAuthContext(): MobileAuthContext | null {
  const platform = sessionStorage.getItem(MOBILE_PLATFORM_KEY)
  const redirect = sessionStorage.getItem(MOBILE_REDIRECT_KEY)

  if (platform === 'mobile' && redirect) {
    return { platform: 'mobile', redirect }
  }

  return null
}

/** Auth navigation keeps the active hosted request explicit on the next entry. */
export function withMobileAuthContext(path: string): string {
  const context = getMobileAuthContext()
  if (!context || !isAllowedMobileRedirect(context.redirect)) return path
  const destination = new URL(path, window.location.origin)
  if (destination.origin !== window.location.origin) return path
  destination.searchParams.set('platform', 'mobile')
  destination.searchParams.set('redirect', context.redirect)
  return destination.pathname + destination.search + destination.hash
}

/**
 * Store mobile auth context from URL params
 */
export function storeMobileAuthContext(
  platform: string | null,
  redirect: string | null,
): boolean {
  if (platform === 'mobile' && redirect) {
    clearMobileAuthContext()
    if (!isAllowedMobileRedirect(redirect)) {
      logger.warn('[MobileAuth] Ignoring untrusted mobile redirect URI')
      clearMobileAuthContext()
      return false
    }

    sessionStorage.setItem(MOBILE_PLATFORM_KEY, 'mobile')
    sessionStorage.setItem(MOBILE_REDIRECT_KEY, redirect)
    return true
  }
  clearMobileAuthContext()
  return false
}

/**
 * Clear mobile auth context
 */
export function clearMobileAuthContext(): void {
  sessionStorage.removeItem(MOBILE_PLATFORM_KEY)
  sessionStorage.removeItem(MOBILE_REDIRECT_KEY)
  sessionStorage.removeItem(MOBILE_OAUTH_STATE_KEY)
  clearMobileStateRetry()
}

/** Associate a hosted browser's native return URI with the OAuth request it made. */
export function rememberMobileOAuthRequest(authUrl: string): void {
  if (!getMobileAuthContext()) return
  const state = new URL(authUrl).searchParams.get('state')
  if (!state) throw new Error('Incomplete mobile sign-in request.')
  sessionStorage.setItem(MOBILE_OAUTH_STATE_KEY, state)
}

/** Direct native OAuth must not inherit a canceled hosted browser's return state. */
export function selectMobileOAuthContext(state: string | null): void {
  if (state && sessionStorage.getItem(MOBILE_OAUTH_STATE_KEY) === state) return
  sessionStorage.removeItem(MOBILE_PLATFORM_KEY)
  sessionStorage.removeItem(MOBILE_REDIRECT_KEY)
  sessionStorage.removeItem(MOBILE_OAUTH_STATE_KEY)
  // The linking retry map has its own exact state association.
}

export interface NativeHandoffResponse {
  nativeHandoff?: { code: string; state: string; expiresIn: number }
}

/** Public request binding only. The device verifier never leaves native secure storage. */
export function mobileHandoffBody(): {
  nativeHandoff?: { state: string; codeChallenge: string }
} {
  if (!isMobileAuthFlow()) return {}
  const context = getMobileAuthContext()
  if (!context || !isAllowedMobileRedirect(context.redirect))
    throw new Error('Start device sign-in again with an updated app.')
  const uri = new URL(context.redirect)
  const state = uri.searchParams.get('state'),
    codeChallenge = uri.searchParams.get('codeChallenge')
  if (
    !state ||
    !/^[A-Za-z0-9_-]{16,128}$/.test(state) ||
    !codeChallenge ||
    !/^[A-Za-z0-9_-]{43}$/.test(codeChallenge) ||
    uri.searchParams.get('codeChallengeMethod') !== 'S256'
  )
    throw new Error('Start device sign-in again with an updated app.')
  return { nativeHandoff: { state, codeChallenge } }
}

/** The callback contains only a short-lived code and the initiating device state. */
export function buildMobileRedirectUrl(
  response: NativeHandoffResponse,
): string | null {
  const context = getMobileAuthContext(),
    handoff = response.nativeHandoff
  if (
    !context ||
    !isAllowedMobileRedirect(context.redirect) ||
    !handoff ||
    typeof handoff.code !== 'string' ||
    !/^[A-Za-z0-9_-]{43}$/.test(handoff.code) ||
    typeof handoff.state !== 'string' ||
    !Number.isSafeInteger(handoff.expiresIn) ||
    handoff.expiresIn <= 0 ||
    handoff.expiresIn > 120
  )
    return null
  try {
    const request = mobileHandoffBody().nativeHandoff!
    if (handoff.state !== request.state) return null
    const redirect = new URL(context.redirect)
    redirect.search = ''
    redirect.searchParams.set('state', request.state)
    redirect.searchParams.set('handoffCode', handoff.code)
    clearMobileAuthContext()
    return redirect.toString()
  } catch {
    return null
  }
}

/** Failure keeps native context available and never falls back to a browser login. */
export function redirectToMobileApp(response: NativeHandoffResponse): boolean {
  const redirectUrl = buildMobileRedirectUrl(response)
  if (!redirectUrl)
    throw new Error(
      'Could not return to your device. Start signing in again with the updated app.',
    )
  window.location.href = redirectUrl
  return true
}
